<template>
    <transition name="entry-loader-animation" appear>
        <div
        ref="spinnerWrapper"
        class="spinner-wrapper dont-close-centered-modals"
        :class="{
            'mobile': getMobileAppState,
            'is-in': getAllCustomerInfo,
            'exit': applyLoginLoaderExitAnimation,
            'simple-exit': triggerLoginSimpleExit
        }">
            <login-particular-svg
            v-if="getClientType === 'particular' && getAppThemeMode === 'light'"
            :key="`svgKey${toogleSvgRerender}`"/>
            <login-particular-dark-svg
            v-if="getClientType === 'particular' && getAppThemeMode === 'dark'"
            :key="`svgKey${toogleSvgRerender}`"/>
            <login-company-svg
            v-if="getClientType === 'company' && getAppThemeMode === 'light'"
            :key="`svgKey${toogleSvgRerender}`"/>
            <login-company-dark-svg
            v-if="getClientType === 'company' && getAppThemeMode === 'dark'"
            :key="`svgKey${toogleSvgRerender}`"/>
            <img
            class="user-profile-pic"
            :class="{'finalize-exit': applyLoginLoaderExitAnimation, 'apply-opacity': applyOpacity}"
            :style="finalizeExitStyles"
            :src="avatarBackgroundUrl"
            @error="errorImage"/>

            <transition name="slide-in">
                <div v-if="getAllCustomerInfo" class="welcome-wrapper" :class="{ 'mobile': getMobileAppState }">
                    <div class="message">{{ t('login.welcome') }}</div>
                    <div class="entity-name">{{ getAllCustomerInfo.displayName }}</div>
                </div>
            </transition>
        </div>
    </transition>
</template>

<script setup>
import loginParticularSvg from '@/components/generic/loaders/loginParticularSvg.svg?inline'
import loginParticularDarkSvg from '@/components/generic/loaders/loginParticularDarkSvg.svg?inline'
import loginCompanySvg from '@/components/generic/loaders/loginCompanySvg.svg?inline'
import loginCompanyDarkSvg from '@/components/generic/loaders/loginCompanyDarkSvg.svg?inline'

/* vue */
import { computed, watch, ref } from 'vue'

/* Store */
import { mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'
import { requestCustomerImageUrl } from '@/network/api/customers/customers.api.js'

/* i18n */
import { useI18n } from 'vue-i18n'

/* Emmitters */
const emit = defineEmits(['closeLoginLoader'])
const { t } = useI18n()

/* mapGetters */
const { getClientType, getAppThemeMode, getMobileAppState, getDeviceInUse } = mapGetters()
const { getAllCustomerInfo } = mapGetters(moduleMapper.SESSION)

/* Props */
const props = defineProps({
    triggerLoginSimpleExit: Boolean,
	applyLoginLoaderExitAnimation: Boolean,
    reRenderSvg: Boolean
})

const triedFetchingImg = ref(false)

const defaultImage = computed(() => {
    return getAppThemeMode.value === 'dark' ? './images/user_image_placeholder_dark.svg' : './images/user_image_placeholder.svg'
})

const avatarBackgroundUrl = computed(() => {
    return getAllCustomerInfo.value ? getAllCustomerInfo.value.customer.avatarImageUrl : defaultImage.value
})

const errorImage = event => {
    if (!triedFetchingImg.value) {
        triedFetchingImg.value = true
        if (process.env.VUE_APP_ENV === 'QUA') {
            event.target.src = requestCustomerImageUrl()
            return
        }
    }

    event.target.src = getAppThemeMode.value === 'dark' ? './images/user_image_placeholder_dark.svg' : './images/user_image_placeholder.svg'
}

const toogleSvgRerender = ref(false)
const finalizeExitStyles = ref(null)
const applyOpacity = ref(false)
const spinnerWrapper = ref()

watch(() => props.applyLoginLoaderExitAnimation, (oldVal, newVal) => {
    if (props.applyLoginLoaderExitAnimation) {
        finalizeExitStyles.value = {
            top: document.getElementById('profile-pic').getBoundingClientRect().y + 40 + 'px',
            left: document.getElementById('profile-pic').getBoundingClientRect().x + (getMobileAppState.value ? 20 : 0) + 'px'
        }

        setTimeout(() => {
            applyOpacity.value = true
        }, 400)

        setTimeout(() => {
            emit('closeLoginLoader')
        }, 2000)
    }
})

watch(() => props.triggerLoginSimpleExit, (oldVal, newVal) => {
    setTimeout(() => {
        emit('closeLoginLoader')
    }, 2000)
})

watch(() => props.reRenderSvg, (newVal, oldVal) => {
    if (getDeviceInUse.value === 'ios') toogleSvgRerender.value = newVal
})
</script>

<style lang="scss" scoped>
.spinner-wrapper {
    @include spinnerOverlayTeleportStyles;
    position: fixed;
    display: flex;
    flex-direction: column;
    align-items: center;

    background-image: var(--gradient-vertical);
    transition: width 0.7s ease-in-out 1s, opacity 0.1s ease-in-out 2s;
    opacity: 1;

    &:not(.exit) {
        transition: opacity 0.3s linear;
    }

    svg {
        width: 160px;
        height: 160px;
        overflow: visible;
        position: absolute;

        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);

        transition: transform 0.3s ease-in-out, opacity 0.3s ease-in-out;

        :deep(circle) {
            fill: none;

            &:not(:last-of-type) {
                stroke: var(--secondary-light-1);
            }
        }

        :deep(circle + circle) {
            stroke-linecap: round;

            animation: spinning 0.8s linear infinite;
        }
    }

    @keyframes spinning {
        100% {
            transform: rotate(360deg);
        }
    }

    .user-profile-pic {
        width: 122px;
        height: 122px;

        object-fit: cover;
        border-radius: 100%;

        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%) scale(1);

        transition: transform 0.3s ease-in-out;

        &.finalize-exit {
            width: 27px;
            height: 27px;
            opacity: 1;

            position: fixed;
            transition: all 1s ease-in-out 1s;

            &.apply-opacity {
                opacity: 0;
            }
        }
    }

    .welcome-wrapper {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, calc(-50% + 120px));
        opacity: 1;

        transition: transform 0.3s ease-in-out, opacity 0.3s ease-in-out;

        .message {
            @include H5-Regular;
            text-align: center;
            line-height: 20px;
            color: #FFFFFF;
        }

        .entity-name {
            @include H1-SemiBold;
            text-transform: capitalize;
            line-height: 45px;
            color: #FFFFFF;
        }

        &.mobile {
            width: 100%;
            text-align: center;

            .entity-name {
                @include H2-Medium;
                line-height: 36px;
            }
        }
    }

    &.is-in {
        svg {
            transform: translate(-50%, calc(-50% - 20px));
        }

        .user-profile-pic {
            transform: translate(-50%, calc(-50% - 20px));
        }
    }

    &.exit, &.simple-exit {
        width: 284px;
        opacity: 0;

        &.mobile {
            width: 0;
        }

        &.simple-exit {
            width: 100%;
        }

        svg {
            opacity: 0;
        }

        .user-profile-pic {
            transform: translate(-50%, calc(-50% - 20px)) scale(1.5);
            animation: bounceIn 0.3s linear;
        }

        .welcome-wrapper {
            opacity: 0;
        }
    }

    .slide-in-enter-from {
        opacity: 0;
        transform: translate(-50%, calc(-50% + 150px));
    }

    .slide-in-leave-to {
        opacity: 1;
        transform: translate(-50%, calc(-50% + 120px));
    }
}

.entry-loader-animation-enter-from {
    opacity: 0;
}

.entry-loader-animation-leave-to {
    opacity: 1;
}

@keyframes bounceIn{
    0%{
        transform: translate(-50%, calc(-50% - 20px)) scale(1) translate3d(0,0,0);
    }
    90%{
        transform: translate(-50%, calc(-50% - 20px)) scale(1.6);
    }
    100%{
        transform: translate(-50%, calc(-50% - 20px)) scale(1.5) translate3d(0,0,0);
    }
}

</style>
